import { useState, useEffect, useCallback, useRef } from "react";
import { keyframes } from "@emotion/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RefreshIcon from "@mui/icons-material/Refresh";
import { checkApiHealth } from "../services/api";
import { isBypassAuthEnabled } from "../utils/bypassAuth";
import { getDevModeType } from "../utils/devModeType";

const POLL_INTERVAL_CONNECTED_MS = 30_000; // Poll every 30s when connected
const POLL_INTERVAL_DISCONNECTED_MS = 30_000; // Poll every 30s when disconnected

// Animations for disconnected state
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// Color cycling animation for connected state - alternates between dark blue and green every 1 second
const colorCycle = keyframes`
  0% { background-color: #1565C0; } /* Dark Blue */
  50% { background-color: #4CAF50; } /* Green */
  100% { background-color: #1565C0; } /* Dark Blue */
`;

export interface DevWorkspaceIndicatorProps {
  isMobile?: boolean;
  /** When true, bottom nav is hidden — position indicator at bottom: 0 */
  isSettingsPage?: boolean;
  /** When true, hide indicator (e.g., during transaction creation/editing) */
  isTransactionPage?: boolean;
  /** When true, hide indicator (e.g., during account create/edit) */
  isAccountFormPage?: boolean;
}

export default function DevWorkspaceIndicator({ isMobile = false, isSettingsPage = false, isTransactionPage = false, isAccountFormPage = false }: DevWorkspaceIndicatorProps): React.ReactElement | null {
  // Track dev mode type in state so component re-renders when it changes
  const [devModeType, setDevModeType] = useState<"web-mobile" | "web-only">(() => {
    return getDevModeType();
  });
  
  // Show indicator when dev mode is enabled (regardless of "web-mobile" or "web-only" mode)
  // Use the state value directly instead of calling isBypassAuthEnabled() which reads from localStorage
  // This ensures we use the reactive state value that updates when devModeType changes
  const isDevModeEnabled = process.env.NODE_ENV === "development" && process.env.REACT_APP_BYPASS_AUTH === "true";
  // Show indicator in both "web-mobile" and "web-only" modes when dev mode is enabled
  // Hide indicator on transaction or account form pages (create/edit)
  const shouldShowIndicator = isDevModeEnabled && !isTransactionPage && !isAccountFormPage;
  
  
  // Listen for localStorage changes to detect dev mode type changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "devModeType" && e.newValue) {
        const newType = e.newValue as "web-mobile" | "web-only";
        setDevModeType(newType);
      }
    };
    
    // Also check periodically in case storage event doesn't fire (same-window changes)
    const interval = setInterval(() => {
      const currentType = getDevModeType();
      setDevModeType(prev => {
        if (prev !== currentType) {
          return currentType;
        }
        return prev;
      });
    }, 10_000); // Check every 10s to avoid scheduler violations from frequent re-renders
    
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const [connected, setConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const prevConnectedRef = useRef<boolean | null>(null);
  const [dotColorIndex, setDotColorIndex] = useState(0); // For blinking: 0 = hidden, 1 = green visible

  const check = useCallback(async () => {
    // Check health regardless of dev mode type (both "web-mobile" and "web-only" should show connection status)
    setIsChecking(true);
    try {
      const ok = await checkApiHealth();
      const prevConnected = prevConnectedRef.current;
      prevConnectedRef.current = ok;
      setConnected(ok);
    } catch (error) {
      // If checkApiHealth throws an error, treat as disconnected
      prevConnectedRef.current = false;
      setConnected(false);
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    // Run health checks when dev mode is enabled (both "web-mobile" and "web-only" modes)
    if (!shouldShowIndicator) {
      return;
    }
    
    // Check immediately on mount
    void check();
  }, [check, shouldShowIndicator]);

  // Separate effect for polling interval that adapts to connection status
  useEffect(() => {
    // Poll when dev mode is enabled (both "web-mobile" and "web-only" modes)
    if (!shouldShowIndicator) return;
    if (connected === null) return; // Wait for initial check
    
    // Poll at different intervals based on connection status
    // Faster polling when disconnected to detect reconnection quickly
    const pollInterval = connected === false 
      ? POLL_INTERVAL_DISCONNECTED_MS 
      : POLL_INTERVAL_CONNECTED_MS;
    
    const id = setInterval(() => {
      void check();
    }, pollInterval);
    return () => clearInterval(id);
  }, [check, connected, shouldShowIndicator]);

  // Blinking green effect for connected state (works better on mobile than CSS animations)
  useEffect(() => {
    // Animate when dev mode is enabled (both "web-mobile" and "web-only" modes)
    if (!shouldShowIndicator) return;
    if (connected !== true) {
      // Reset to 0 when not connected
      setDotColorIndex(0);
      return;
    }
    
    // Blink green (show/hide) every 1.2 seconds to reduce main-thread work and scheduler violations
    setDotColorIndex(1);
    const colorInterval = setInterval(() => {
      setDotColorIndex(prev => (prev === 0 ? 1 : 0));
    }, 1200);
    
    return () => clearInterval(colorInterval);
  }, [connected, shouldShowIndicator]);

  // Show indicator when dev mode is enabled (both "web-mobile" and "web-only" modes)
  
  // Early return if we shouldn't show the indicator
  // Only hide if dev mode is not enabled
  if (!shouldShowIndicator) {
    return null;
  }

  const statusText =
    connected === null || isChecking
      ? "Checking…"
      : connected
        ? "Connected to dev API"
        : "Backend disconnected";

  // Determine dot color: blink green (show/hide) when connected, red blinking when disconnected
  const getDotColor = (): string => {
    if (connected === null) return "text.secondary";
    if (connected === false) return "error.main";
    // When connected, blink green (show when dotColorIndex === 1, hide when 0)
    return dotColorIndex === 1 ? "#4CAF50" : "transparent"; // Green or transparent
  };
  
  const dotColor = getDotColor();

  const isDisconnected = connected === false;
  const isCheckingState = connected === null || isChecking;

  return (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: isMobile && !isSettingsPage ? 76 : 0, // Above bottom nav on mobile (20 + 56); at bottom on Settings
        height: isDisconnected ? 28 : 20, // Taller when disconnected for visibility
        zIndex: 1100, // Higher than bottom nav (1000) to ensure visibility on mobile
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        bgcolor: isDisconnected ? "error.dark" : "background.paper",
        borderTop: 2,
        borderColor: isDisconnected ? "error.main" : "divider",
        px: 1,
        // Pulse animation when disconnected
        ...(isDisconnected && {
          boxShadow: "0 -2px 8px rgba(211, 47, 47, 0.3)", // Red shadow for visibility
          animation: `${pulse} 2s ease-in-out infinite`,
        }),
      }}
    >
      <Box
        sx={{
          width: isDisconnected ? 8 : 8, // Make it bigger (8px) when connected too for visibility
          height: isDisconnected ? 8 : 8,
          borderRadius: "50%",
          flexShrink: 0,
          // Use state-based color cycling when connected (works better on mobile), red blinking when disconnected
          ...(isDisconnected 
            ? {
                bgcolor: dotColor,
                animation: `${blink} 1s ease-in-out infinite`,
              }
            : {
                // Use state-based color change (more reliable on mobile than CSS animations)
                // Use backgroundColor directly for better mobile browser support
                backgroundColor: dotColor,
                transition: "background-color 0.5s ease-in-out", // Smooth color transition
                // Force repaint on mobile browsers
                willChange: "background-color",
                // Ensure it's visible
                minWidth: "8px",
                minHeight: "8px",
              }
          ),
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize: isDisconnected ? "0.75rem" : "0.7rem",
          color: isDisconnected ? "error.contrastText" : "text.secondary",
          fontWeight: isDisconnected ? 600 : 500,
        }}
      >
        Dev workspace · {statusText}
      </Typography>
      {/* Always show refresh button for manual testing */}
      <Tooltip title={isDisconnected ? "Check connection now" : "Refresh connection status"}>
        <span>
          <IconButton
            size="small"
            onClick={() => {
              void check();
            }}
            disabled={isCheckingState}
            sx={{
              color: isDisconnected ? "error.contrastText" : "text.secondary",
              p: 0.5,
              "&:hover": {
                bgcolor: isDisconnected ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
              },
              "&.Mui-disabled": {
                color: isDisconnected ? "error.contrastText" : "text.secondary",
                opacity: 0.5,
              },
            }}
          >
            <RefreshIcon sx={{ fontSize: "0.9rem" }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}
